import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PortfolioSummary.scss';
import { useDispatchPortfolioSummaryInitialise } from 'widgets/portfolioSummary/store/instances/dispatcher';
import { usePortfolioActiveInputUsesOrdersStream } from 'portfolio/store/hooks';

import PortfolioSummaryGrid from './grid/PortfolioSummaryGrid';
import PortfolioSummaryToolbar from './toolbar/PortfolioSummaryToolbar';

import { usePortfolioSummaryConfigurationSelector } from './store/instances/configuration/selectors';
import PortfolioSummaryChart from './chart/PortfolioSummaryChart';
import styleConstants from 'styles/variables.module.scss';
import PortfolioSummaryTreemap from './treemap/PortfolioSummaryTreemap';
import SelectionStatus from 'portfolio/components/SelectionStatus';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks';
import { useWidgetContext } from 'widgets/WidgetContext';
import { GridApi } from '@instinet-analytics/semantic-ui-components';
import { PubSub } from '@instinet-analytics/core-lib';
import { useWidgetTitle } from 'widgets/tabTitle/hooks';

const padding = 2 * parseInt(styleConstants.spaceAround);
const toolbarHeight = parseInt(styleConstants.panelHeaderHeight);
const borderWidth = 2 * parseInt(styleConstants.layoutBorderWidth);

export default function PortfolioSummary() {
    const { widgetKey, width, height } = useWidgetContext();

    const changedFilterRef = useRef(new PubSub<void>());

    const dispatchPortfolioSummaryInitialise =
        useDispatchPortfolioSummaryInitialise();

    useEffect(() => {
        dispatchPortfolioSummaryInitialise();
    }, [dispatchPortfolioSummaryInitialise]);

    const config = usePortfolioSummaryConfigurationSelector();
    const { aggregationField, view, hideToolbar } = config;
    const [gridApi, setGridApi] = useState<GridApi | undefined>();
    const title = useWidgetTitle();
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    const { portfolioSelectionRequirements } =
        useWidgetPortfolioSelectionStatus();

    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();

    const toolbarConsumedHeight = hideToolbar ? 0 : toolbarHeight;
    const contentTop = padding / 2 + borderWidth + toolbarConsumedHeight;
    const contentHeight = height - contentTop - padding / 2;

    const contentWidth = width - padding - borderWidth;
    const contentStyle = {
        height: `${contentHeight}px`,
        top: `${contentTop}px`,
    };

    const onSelectedValuesChanged = useCallback(
        (nextSelectedValues: string[]) => {
            setSelectedValues(nextSelectedValues);
        },
        [setSelectedValues]
    );

    return (
        <div className="PortfolioSummary" data-widget-id={widgetKey}>
            <div className="PortfolioSummaryContainer">
                <PortfolioSummaryToolbar
                    gridApi={gridApi}
                    title={title}
                    selectedValues={selectedValues}
                    changedFilterRef={changedFilterRef.current}
                />
                {!activeInputUsesOrdersStream && (
                    <SelectionStatus
                        selectionRequirements={portfolioSelectionRequirements}
                        widgetName={'Portfolio Summary Widget'}
                    />
                )}
                {activeInputUsesOrdersStream && (
                    <div
                        className="PortfolioSummaryContent"
                        style={contentStyle}
                    >
                        {view === 'grid' && (
                            <PortfolioSummaryGrid
                                aggregationField={aggregationField}
                                changedFilterRef={changedFilterRef.current}
                                onGridApiSet={setGridApi}
                                onSelectedValuesChanged={
                                    onSelectedValuesChanged
                                }
                            />
                        )}
                        {view === 'chart' && (
                            <PortfolioSummaryChart
                                height={contentHeight}
                                config={config}
                            />
                        )}
                        {view === 'treemap' && (
                            <PortfolioSummaryTreemap
                                height={contentHeight}
                                width={contentWidth}
                                config={config}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
